import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import api from '@utils/api';
import formutils from '@utils/formutils';
import urlUtils from '@utils/urlUtils';
import { getStep } from '@state/features/accordion.selectors';
import { Form } from 'react-final-form';
import Button from '@ui/button/Button';
import DwFormField from '@ui/formfield/DwFormField';
import Loading from '@ui/loading/Loading';
import { getFirstFocusable } from 'src/scripts/utilities/focusableElementUtils';
import { AccordionStepsActionTypes, ConnectivityActionTypes } from '@state/features/common.types';
const addressfields = ['firstName', 'lastName', 'fiscalCode', 'birthDate', 'birthPlace', 'phone', 'email'];
const ConnectivityRegistryForm = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { product, registry, customer } = useSelector((state) => state.connectivity);
    const step = useSelector(getStep) || 'personalData';
    const [formdef, setFormdef] = useState();
    const [initialValues, setInitialValues] = useState();
    const registryFormRef = useRef(null);
    useEffect(() => {
        let val = {};
        api.getForm('registryConnectivity', val).then((data) => {
            if (registry && !customer.profile) {
                val.firstName = registry.firstName;
                val.lastName = registry.lastName;
                val.fiscalCode = registry.fiscalCode;
                val.birthDate = new Date(registry.birthDate).toISOString().slice(0, 10);
                val.birthPlace = registry.birthPlace;
                val.phone = registry.phone;
                val.email = registry.email;
            }
            else if (customer.profile && customer.registeredUser) {
                val.firstName = customer.profile.firstName;
                val.lastName = customer.profile.lastName;
                val.fiscalCode = customer.profile.fiscalCode;
                val.birthDate = customer.profile.birthday ? new Date(customer.profile.birthday).toISOString().slice(0, 10) : '';
                val.birthPlace = customer.profile.birthPlace ? customer.profile.birthPlace : '';
                val.phone = String(customer.profile.phone);
                val.email = customer.profile.email;
            }
            setInitialValues(Object.assign(Object.assign({}, initialValues), val));
            setFormdef(data);
        });
    }, []);
    const validate = async (values) => {
        let validationForm = await formutils.validateForm(values, formdef, 'registryConnectivity');
        return validationForm;
    };
    const onSubmit = async (values) => {
        props.setErrors([]);
        urlUtils.removeErrorParamCheckout(step);
        let err = await validate(values);
        if (err && Object.keys(err).length > 0) {
            return err;
        }
        return api
            .connectivitySubmitRegistry(values, formdef)
            .then((data) => {
            if (!formutils.checkValidationAfterSubmit(data, err)) {
                return err;
            }
            if (data.serverErrors && data.serverErrors.length > 0) {
                props.setErrors(data.serverErrors);
                return;
            }
            if (data.error) {
                if (data.redirectUrl) {
                    window.location.href = data.redirectUrl;
                    return;
                }
                props.setErrors(['unknown error']); // temp
                return;
            }
            dispatch({ type: ConnectivityActionTypes.UPDATE_REGISTRY, payload: data.registry });
            dispatch({ type: AccordionStepsActionTypes.NAVIGATE_STEP, payload: 'lineData' });
        })
            .catch((e) => props.setErrors([e]));
    };
    if (!product || !formdef) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement("div", { className: 'checkout-connectivity-registry' },
        React.createElement("p", { className: 'checkout-step-description' }, t('connectivity.checkout.personaldata.description')),
        React.createElement(Form, { onSubmit: onSubmit, initialValues: initialValues, validate: validate, mutators: {
                global: formutils.globalMutator,
                setValue: ([selected], state, { changeValue }) => {
                    var _a, _b;
                    addressfields.forEach((f) => {
                        changeValue(state, f, () => selected[f]);
                    });
                    if ((_a = selected === null || selected === void 0 ? void 0 : selected.countryCode) === null || _a === void 0 ? void 0 : _a.value) {
                        let newcountry = (_b = selected === null || selected === void 0 ? void 0 : selected.countryCode) === null || _b === void 0 ? void 0 : _b.value;
                        if (formdef.country.options.find((o) => o.value == newcountry)) {
                            // @ts-ignore
                            let previouscountry = state.formState.values.country;
                            if (newcountry != previouscountry) {
                                // console.info('changing country code to ' + newcountry + ' from ' + previouscountry);
                                changeValue(state, 'country', () => { var _a; return (_a = selected === null || selected === void 0 ? void 0 : selected.countryCode) === null || _a === void 0 ? void 0 : _a.value; });
                            }
                        }
                        else {
                            // console.log('NOT changing country code to ' + newcountry + ' because not available in select options');
                        }
                    }
                },
            }, render: ({ handleSubmit, errors, form, submitting, values }) => (React.createElement("form", { ref: registryFormRef, onSubmit: (e) => {
                    handleSubmit(e);
                    if (errors && Object.keys(errors).length > 0) {
                        setTimeout(() => {
                            var _a;
                            const invalidElements = (_a = registryFormRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('.is-invalid');
                            if ((invalidElements === null || invalidElements === void 0 ? void 0 : invalidElements.length) > 0) {
                                invalidElements[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
                                setTimeout(() => { var _a; return (_a = getFirstFocusable(invalidElements[0])) === null || _a === void 0 ? void 0 : _a.focus(); }, 500);
                            }
                        }, 150);
                    }
                } },
                React.createElement("div", { className: 'address-form' },
                    React.createElement("div", { className: 'field-row' },
                        React.createElement(DwFormField, { def: formdef, name: "firstName", placeholder: t('label.placeholder.firstname'), readonly: customer.profile && customer.profile.firstName ? true : false }),
                        React.createElement(DwFormField, { def: formdef, name: "lastName", placeholder: t('label.placeholder.lastname'), readonly: customer.profile && customer.profile.lastName ? true : false })),
                    React.createElement("div", { className: 'field-row' },
                        React.createElement(DwFormField, { def: formdef, name: "fiscalCode", placeholder: t('label.placeholder.fiscalcode'), readonly: customer.profile && customer.profile.fiscalCode ? true : false }),
                        React.createElement(DwFormField, { def: formdef, name: "birthDate", type: 'date', readonly: customer.profile && customer.profile.birthday ? true : false })),
                    React.createElement("div", { className: 'field-row' },
                        React.createElement(DwFormField, { def: formdef, name: "birthPlace", placeholder: t('label.placeholder.birthplace'), readonly: customer.profile && customer.profile.birthPlace ? true : false }),
                        React.createElement(DwFormField, { def: formdef, name: "phone", placeholder: t('label.placeholder.phone'), readonly: customer.profile && customer.profile.phone ? true : false })),
                    React.createElement("div", { className: 'field-row' },
                        React.createElement(DwFormField, { def: formdef, name: "email", placeholder: t('label.placeholder.email'), readonly: customer.profile && customer.profile.email ? true : false })),
                    React.createElement("p", { className: classNames('info-mandatory-label') }, t('label.info.mandatory'))),
                React.createElement("div", { className: 'email-info-container' },
                    React.createElement("span", { className: 'icon-container' },
                        React.createElement("svg", { className: `icon--mail icon--m no-stroke`, "aria-hidden": "true" },
                            React.createElement("use", { href: `#sprite-mail` }))),
                    React.createElement("p", { dangerouslySetInnerHTML: { __html: t('connectivity.checkout.personaldata.emailbox') } })),
                React.createElement("div", { className: 'checkout-navigations' },
                    React.createElement(Button, { type: "submit", cls: ['cta-primary'], isAnimated: true, style: { pointerEvents: submitting ? 'none' : 'initial', cursor: submitting ? 'not-allowed' : 'pointer' } },
                        submitting && React.createElement(Loading, { inline: true }),
                        React.createElement("span", { style: { opacity: submitting ? 0 : 1 } }, t('common.proceed')))))) })));
};
export default ConnectivityRegistryForm;
